import React, { useState, useMemo, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, X, Users, TrendingUp, Award, Hand, Move, Check, ChevronsRight, LogOut, Sparkles } from "lucide-react";
import { Panel, Eyebrow, MonoLabel, LiveDot } from "@/components/Panel";
import { ROUNDS, SEATING, TABLES_COUNT, MEMBERS as SEED_MEMBERS, findMember as findSeedMember } from "@/data/seed";
import { useDemo } from "@/store/DemoStore";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { useReferralsLive, postReferral, promoteReferral, verifyLeaderPin, startLeaderSession, pingLeaderSession, endLeaderSession, deleteReferral, newIdempotencyKey } from "@/lib/referralsApi";

const EVENT_SLUG = "davangere-members-day-2026";

// live members hook — pulls real paid registrations from the backend and returns a stable findMember()
function useLiveMembers() {
  const [live, setLive] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/leader/members`, { params: { event_slug: EVENT_SLUG } })
      .then((r) => setLive(r.data.members || []))
      .catch(() => setLive([]));
  }, []);
  const findMember = (seatId) => {
    if (!live || live.length === 0) return findSeedMember(seatId);
    // Exact match on real registration IDs first (handles the viewing member when
    // their id is passed via ?highlight=<reg_id>).
    const exact = live.find((m) => m.id === seatId || m.reg_id === seatId);
    if (exact) return exact;
    // seat IDs like "M001"..."M050" (from seed) → map by positional index into live members (mod length)
    const n = parseInt(String(seatId).replace(/[^0-9]/g, ""), 10);
    if (Number.isFinite(n) && n > 0) return live[(n - 1) % live.length] || findSeedMember(seatId);
    return findSeedMember(seatId);
  };
  return { live, findMember, ready: live !== null };
}

export default function TableLeader() {
  const { state, dispatch } = useDemo();
  const { findMember, live, ready } = useLiveMembers();
  const [searchParams] = useSearchParams();
  const [leader, setLeader] = useState(() => {
    try { return JSON.parse(localStorage.getItem("bni-leader") || "null"); } catch { return null; }
  });
  const initialRound = Math.max(1, Math.min(4, Number(searchParams.get("round")) || 1));
  const initialTable = Math.max(1, Math.min(TABLES_COUNT, Number(searchParams.get("table")) || 4));
  const highlightId = searchParams.get("highlight") || null;
  const viewOnly = searchParams.get("view") === "1";
  const [round, setRound] = useState(initialRound);
  const [table, setTable] = useState(initialTable);
  const [pending, setPending] = useState(null);
  const [tapSrc, setTapSrc] = useState(null);

  useEffect(() => {
    if (highlightId) {
      // Refresh round/table selection if the URL params change
      setRound(initialRound);
      setTable(initialTable);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const seats = SEATING[round][table - 1].seats;
  const membersHere = useMemo(() => {
    let rows = seats.map(s => ({ ...s, member: findMember(s.memberId) }));
    // Ensure the viewing member always appears at this table — the seed seating
    // can't include freshly registered members, so inject them whenever
    // ?highlight=<memberId> is present and absent from seats.
    if (highlightId && !rows.some(r => r.memberId === highlightId)) {
      const injected = findMember(highlightId);
      if (injected) {
        const last = rows.length - 1;
        rows[last] = { ...rows[last], memberId: highlightId, member: injected, injected: true };
      }
    }
    // Always render the logged-in member at the TOP CENTER of the round table
    // (seat index 0 maps to angle -π/2, which sits at 12 o'clock). Rotate the
    // seat order so the highlighted member leads without reshuffling the rest.
    if (highlightId) {
      const idx = rows.findIndex(r => r.memberId === highlightId);
      if (idx > 0) rows = [...rows.slice(idx), ...rows.slice(0, idx)];
    }
    return rows;
  }, [seats, findMember, highlightId]);

  // Live referrals from the backend (replaces DemoStore for the on-screen counts).
  const { items: liveRefs, refresh: refreshRefs } = useReferralsLive({ table, round, intervalMs: 3000 });
  const roundRefs = liveRefs.filter(r => r.round === round && r.table === table);
  // Ping the server every 2 min so leader-sessions.last_seen_at stays fresh.
  useEffect(() => {
    if (!leader?.session_id) return;
    const iv = setInterval(() => pingLeaderSession(leader.session_id), 120_000);
    return () => clearInterval(iv);
  }, [leader?.session_id]);
  const roundTotalCount = roundRefs.reduce((a, r) => a + r.count, 0);
  const roundTotalValue = roundRefs.reduce((a, r) => a + r.value * r.count, 0);

  const perMember = useMemo(() => {
    const map = {};
    membersHere.forEach(m => map[m.memberId] = { given: 0, received: 0 });
    roundRefs.forEach(r => {
      if (map[r.from]) map[r.from].given += r.count;
      if (map[r.to])   map[r.to].received += r.count;
    });
    return map;
  }, [roundRefs, membersHere]);

  if (!leader && !viewOnly) return <LeaderGate onOk={(d) => { localStorage.setItem("bni-leader", JSON.stringify(d)); setLeader(d); }} />;

  const signOut = async () => {
    if (!window.confirm("Sign out of this leader session? The next person will need the PIN to log referrals.")) return;
    try {
      if (leader?.session_id) await endLeaderSession(leader.session_id);
    } catch { /* non-fatal — clear locally regardless */ }
    localStorage.removeItem("bni-leader");
    setLeader(null);
    setTapSrc(null);
    setPending(null);
    toast.success("Signed out — hand the tablet to the next leader");
  };

  const topGiver = Object.entries(perMember).sort((a,b)=>b[1].given - a[1].given)[0];

  const handleDrop = (fromId, toId) => {
    if (viewOnly) return;
    // A referral can only be logged FROM the signed-in member's own seat, not
    // on anyone else's behalf. The leader session identifies that owner.
    if (highlightId && fromId !== highlightId) {
      toast.error("You can only log referrals FROM your own seat");
      return;
    }
    if (!fromId || !toId || fromId === toId) return;
    setPending({ from: fromId, to: toId });
  };

  const handleTap = (id) => {
    if (viewOnly) return;
    if (!tapSrc) {
      // Enforce "source = me" at tap time too, so the UI is consistent with drag.
      if (highlightId && id !== highlightId) {
        toast.error("Tap your own seat first — you can only log referrals you gave");
        return;
      }
      setTapSrc(id); toast("Now tap the destination member", { icon: <ChevronsRight size={14} style={{color:"var(--red-2)"}}/> }); return;
    }
    if (tapSrc === id) { setTapSrc(null); return; }
    handleDrop(tapSrc, id);
    setTapSrc(null);
  };

  const confirmReferral = async (count, type, value) => {
    if (!pending) return;
    const client_idempotency_key = newIdempotencyKey();
    try {
      await postReferral({
        round, table, from: pending.from, to: pending.to, count, type,
        value_paise: Math.round(value * 100),
        logged_by_name: leader?.name || undefined,
        logged_by_phone: leader?.phone || undefined,
        client_idempotency_key,
      });
      refreshRefs();
      const fromM = findMember(pending.from), toM = findMember(pending.to);
      toast.success(`${(fromM?.name || pending.from).split(' ')[0]} → ${(toM?.name || pending.to).split(' ')[0]} · ${count} logged`);
    } catch (e) {
      const msg = e?.response?.data?.detail?.errors?.[0]?.message || e?.response?.data?.detail || "Failed to log referral — try again";
      toast.error(typeof msg === "string" ? msg : "Failed to log referral");
    }
    setPending(null);
  };

  const undoReferral = async (ref) => {
    if (!ref?.id) return;
    if (!window.confirm(`Undo this referral (${ref.count} × ${ref.type})?`)) return;
    try {
      await deleteReferral(ref.id, leader?.name || undefined);
      refreshRefs();
      toast.success("Referral undone");
    } catch (e) {
      const msg = e?.response?.data?.detail?.errors?.[0]?.message || e?.response?.data?.detail || "Undo failed";
      toast.error(typeof msg === "string" ? msg : "Undo failed");
    }
  };

  const promoteOne = async (ref) => {
    if (!ref?.id) return;
    try {
      const r = await promoteReferral(ref.id, {
        leader_name: leader?.name || undefined,
        leader_phone: leader?.phone || undefined,
      });
      toast.success(r.message || "Promoted to Big Screen!", {
        icon: <Sparkles size={14} style={{ color: "var(--red-2)" }} />,
        duration: 4500,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail?.errors?.[0]?.message || e?.response?.data?.detail || "Promote failed";
      toast.error(typeof msg === "string" ? msg : "Promote failed");
    }
  };

  return (
    <div className="min-h-screen">
      <Toaster theme="dark" position="top-right" toastOptions={{
        style: { background: "var(--panel)", border: "1px solid var(--hairline)", color: "var(--text)", borderRadius: 0 }
      }} />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <Eyebrow>{viewOnly ? "My Table · View" : "Table Leader · Command"}</Eyebrow>
              {viewOnly && (
                <button
                  data-testid="table-view-back-btn"
                  onClick={() => (window.location.href = "/member")}
                  className="btn-ghost !py-1.5 !px-3 text-xs shrink-0"
                  title="Back to your member portal"
                >
                  <LogOut size={12} style={{ transform: "rotate(180deg)" }} /> <span>Back to portal</span>
                </button>
              )}
            </div>
            <h1 className="h-hero mt-3 text-4xl sm:text-5xl">Table {String(table).padStart(2,"0")}</h1>
            <p className="mt-2 max-w-[520px]" style={{ color: "var(--body)" }}>
              {viewOnly
                ? "You're viewing your table seating. Only the table leader can log referrals during the event."
                : "Drag your own avatar onto another member to log a referral you gave. Or tap your seat, then tap the destination."}
            </p>
          </div>
          {!viewOnly && (
            <div className="flex flex-col gap-3 items-end">
              <div className="flex items-center gap-3">
                <LiveDot label="LIVE ROUND" />
                {leader?.name && (
                  <span className="mono-label hidden sm:inline" style={{ color: "var(--taupe)" }}>
                    · {leader.name.split(" ")[0]}
                  </span>
                )}
                <button
                  data-testid="leader-signout-btn"
                  onClick={signOut}
                  className="btn-ghost !py-1.5 !px-3 text-xs"
                  title="Sign out — clears this leader session for a clean tablet handoff"
                >
                  <LogOut size={12} /> <span>Sign out</span>
                </button>
              </div>
              <TableJumper table={table} setTable={setTable} />
            </div>
          )}
          {viewOnly && (
            <div className="w-full sm:w-auto">
              <LiveDot label="VIEW ONLY" />
            </div>
          )}
        </div>

        {/* Round tabs */}
        <div className="mt-8 flex hairline overflow-x-auto">
          {ROUNDS.map((r) => (
            <button
              key={r.id}
              data-testid={`round-tab-${r.id}`}
              onClick={() => { setRound(r.id); setTapSrc(null); }}
              className={`flex-1 min-w-[140px] px-4 py-3 text-left border-r border-[var(--hairline)] last:border-r-0 ${round === r.id ? "bg-[var(--panel)]" : ""}`}
              style={{ borderTop: round === r.id ? "2px solid var(--red)" : "2px solid transparent" }}
            >
              <div className="mono-label" style={{ color: round === r.id ? "var(--red)" : "var(--taupe)" }}>{r.label}</div>
              <div className="text-sm mt-1">{r.time}</div>
            </button>
          ))}
        </div>

        {/* Layout */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Panel className="p-6 lg:col-span-8 flex flex-col items-center">
            <div className="w-full flex items-center justify-between">
              <MonoLabel>Round {round} · Seat Map</MonoLabel>
              <div className="flex items-center gap-3">
                {!viewOnly && <span className="chip"><Move size={10} /> DRAG</span>}
                {!viewOnly && <span className="chip"><Hand size={10} /> OR TAP</span>}
                {viewOnly && <span className="chip">READ-ONLY</span>}
              </div>
            </div>

            <RoundTable
              members={membersHere}
              perMember={perMember}
              onDrop={handleDrop}
              onTap={handleTap}
              tapSrc={tapSrc}
              tableNo={table}
              highlightId={highlightId}
            />

            <div className="w-full grid grid-cols-3 gap-4 mt-6">
              <Stat k="Referrals this round" v={roundTotalCount} />
              <Stat k="Business volume" v={`₹${(roundTotalValue/100000).toFixed(1)}L`} />
              <Stat k="Top giver" v={topGiver && topGiver[1].given > 0 ? findMember(topGiver[0]).name.split(' ')[0] : "—"} accent="red" />
            </div>
          </Panel>

          <div className="lg:col-span-4 space-y-6">
            <Panel className="p-6">
              <MonoLabel>Members at this table</MonoLabel>
              <div className="mt-4 space-y-3">
                {membersHere.map((m) => (
                  <div key={m.memberId} className="flex items-center gap-3 hairline p-2.5">
                    <img src={m.member.avatar} className="w-10 h-10 object-cover border border-[var(--hairline)]" alt="" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{m.member.name}</div>
                      <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>{m.member.chapter} · {m.member.category}</div>
                    </div>
                    <div className="text-right">
                      <div className="mono-label" style={{ color: "var(--taupe)" }}>Given</div>
                      <div className="font-serif text-base" style={{ color: "var(--red-2)" }}>{perMember[m.memberId]?.given || 0}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel className="p-6">
              <MonoLabel>Recent at this table</MonoLabel>
              <div className="mt-3 max-h-[240px] overflow-auto pr-1 space-y-2">
                {roundRefs.slice(0, 12).length === 0 && <div className="mono-label" style={{color:"var(--muted)"}}>No referrals yet — drag to log one.</div>}
                {roundRefs.slice(0, 12).map((r) => {
                  const f = findMember(r.from), t = findMember(r.to);
                  const ageMs = Date.now() - (r.ts || Date.now());
                  const canUndo = ageMs < 5 * 60 * 1000;
                  return (
                    <div key={r.id} className="text-xs flex items-center gap-2 hairline p-2" data-testid={`ref-row-${r.id.slice(0, 8)}`}>
                      <span className="mono-label" style={{ color: "var(--red)" }}>+{r.count}</span>
                      <span className="truncate">{f?.name.split(' ')[0]} → {t?.name.split(' ')[0]}</span>
                      <span className="ml-auto mono-label" style={{color:"var(--taupe)"}}>₹{(r.value/1000).toFixed(0)}k</span>
                      {!viewOnly && (
                        <button onClick={() => promoteOne(r)} className="mono-label hover:text-[var(--red)] flex items-center gap-1"
                          data-testid={`ref-promote-${r.id.slice(0, 8)}`}
                          title="Spotlight this referral on the Big Screen"
                          style={{ color: "var(--red-2)" }}>
                          <Sparkles size={11} /> PROMOTE
                        </button>
                      )}
                      {canUndo && !viewOnly && (
                        <button onClick={() => undoReferral(r)} className="mono-label hover:text-[var(--red)]"
                          data-testid={`ref-undo-${r.id.slice(0, 8)}`} title="Undo (within 5 min)">
                          UNDO
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </Panel>
          </div>
        </div>
      </div>

      <ReferralModal pending={pending} onClose={() => setPending(null)} onConfirm={confirmReferral} />
    </div>
  );
}

function TableJumper({ table, setTable }) {
  return (
    <div className="flex items-center gap-2">
      <span className="mono-label" style={{ color: "var(--taupe)" }}>Table</span>
      <select
        data-testid="table-jumper"
        value={table}
        onChange={(e) => setTable(Number(e.target.value))}
        className="input-x !py-2 !w-24"
      >
        {Array.from({length: TABLES_COUNT}, (_, i) => i+1).map(n => <option key={n} value={n}>#{n}</option>)}
      </select>
    </div>
  );
}

function Stat({ k, v, accent }) {
  const c = accent === "red" ? "var(--red-2)" : "var(--text)";
  return (
    <div className="hairline p-4">
      <div className="mono-label">{k}</div>
      <div className="font-serif text-2xl mt-2" style={{ color: c }}>{v}</div>
    </div>
  );
}

// ------------- Round Table with Drag/Drop -----------------
function RoundTable({ members, perMember, onDrop, onTap, tapSrc, tableNo, highlightId }) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ w: 460, h: 460 });
  const seatCount = Math.max(members.length, 6);

  useEffect(() => {
    const onResize = () => {
      if (!containerRef.current) return;
      const w = Math.min(containerRef.current.clientWidth, 520);
      setSize({ w, h: w });
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const cx = size.w / 2, cy = size.h / 2;
  const radius = size.w * 0.36;
  const seatSize = Math.max(64, size.w * 0.16);

  // Refs to seat centers for hit-testing
  const seatCentersRef = useRef({});

  return (
    <div ref={containerRef} className="mt-6 relative w-full" style={{ maxWidth: 520, height: size.h }}>
      {/* Center: table */}
      <div
        className="absolute rounded-full flex items-center justify-center"
        style={{
          left: cx, top: cy, transform: "translate(-50%, -50%)",
          width: radius * 1.35, height: radius * 1.35,
          background: "radial-gradient(circle, #1A1E24 0%, #0E1014 80%)",
          border: "1px solid var(--hairline)",
        }}
      >
        <div className="text-center">
          <div className="mono-label" style={{ color: "var(--taupe)" }}>Round Table</div>
          <div className="font-serif text-3xl mt-1" style={{ color: "var(--red-2)" }}>#{tableNo}</div>
        </div>
      </div>

      {members.map((m, i) => {
        const angle = (i / seatCount) * Math.PI * 2 - Math.PI / 2;
        const x = cx + radius * Math.cos(angle);
        const y = cy + radius * Math.sin(angle);
        const isMe = highlightId === m.memberId;
        return (
          <SeatAvatar
            key={m.memberId}
            member={m.member}
            given={perMember[m.memberId]?.given || 0}
            x={x} y={y} size={seatSize}
            selected={tapSrc === m.memberId}
            highlight={isMe}
            draggable={isMe}
            onTap={() => onTap(m.memberId)}
            onDragDrop={(destId) => onDrop(m.memberId, destId)}
            registerCenter={(rect) => seatCentersRef.current[m.memberId] = rect}
            seatCenters={seatCentersRef}
            memberId={m.memberId}
          />
        );
      })}
    </div>
  );
}

function SEATING_ROUND_ID(members) { return 0; } // legacy, unused

function SeatAvatar({ member, given, x, y, size, selected, highlight, onTap, onDragDrop, seatCenters, memberId, draggable = true }) {
  const ref = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [dx, setDx] = useState(0);
  const [dy, setDy] = useState(0);

  useEffect(() => {
    const update = () => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      seatCenters.current[memberId] = r;
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  });

  const onPointerDown = (e) => {
    e.preventDefault();
    // Non-draggable seats still react to a plain tap (destination picking),
    // but never initiate a drag or seat-source selection.
    if (!draggable) { onTap(); return; }
    setDragging(true);
    const start = { x: e.clientX, y: e.clientY };
    let moved = false;
    const el = ref.current;

    const move = (ev) => {
      const nx = ev.clientX - start.x, ny = ev.clientY - start.y;
      if (Math.abs(nx) + Math.abs(ny) > 6) moved = true;
      setDx(nx); setDy(ny);
    };
    const up = (ev) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setDragging(false);
      setDx(0); setDy(0);
      if (!moved) { onTap(); return; }
      // Hide self so elementFromPoint returns underlying seat
      const prevPE = el ? el.style.pointerEvents : "";
      if (el) el.style.pointerEvents = "none";
      const target = document.elementFromPoint(ev.clientX, ev.clientY);
      if (el) el.style.pointerEvents = prevPE;
      const seatEl = target?.closest?.("[data-seat-id]");
      const destId = seatEl?.getAttribute("data-seat-id");
      if (destId && destId !== memberId) onDragDrop(destId);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div
      ref={ref}
      data-seat-id={memberId}
      data-testid={`seat-${memberId}`}
      className={`seat ${highlight ? "seat-blink" : ""}`}
      style={{
        left: x, top: y, width: size, zIndex: dragging ? 30 : (highlight ? 10 : 5),
        transform: `translate(-50%, -50%) translate(${dx}px, ${dy}px)`,
        transition: dragging ? "none" : "transform 0.2s ease",
        cursor: draggable ? (dragging ? "grabbing" : "grab") : "default",
        touchAction: draggable ? "none" : "manipulation",
      }}
      onPointerDown={onPointerDown}
    >
      <div
        className="relative flex flex-col items-center"
        style={{ opacity: dragging ? 0.85 : 1 }}
      >
        <div
          className="rounded-full overflow-hidden border-2 seat-ring"
          style={{
            width: size * 0.82, height: size * 0.82,
            borderColor: highlight ? "var(--red)" : selected ? "var(--red)" : "var(--hairline)",
            boxShadow: highlight
              ? "0 0 22px rgba(255,76,76,0.85)"
              : selected ? "0 0 18px rgba(204,37,47,0.5)" : "none",
          }}
        >
          <img src={member.avatar} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
        </div>
        {highlight && (
          <span
            className="absolute -top-2 left-1/2 -translate-x-1/2 font-mono text-[9px] px-1.5 py-0.5"
            style={{ background: "var(--red)", color: "var(--text)", letterSpacing: "0.14em" }}
          >
            YOU
          </span>
        )}
        {given > 0 && (
          <span
            className="absolute -top-1 -right-1 font-mono text-[10px] px-1.5 py-0.5"
            style={{ background: "var(--red)", color: "var(--text)" }}
          >
            +{given}
          </span>
        )}
        <div className="mt-2 text-center max-w-[110px]">
          <div className="text-[11px] font-medium truncate leading-tight" style={{ color: highlight ? "var(--red-2)" : "var(--text)" }}>{member.name.split(' ')[0]}</div>
          <div className="mono-label truncate" style={{ color: "var(--taupe)", fontSize: 9 }}>{member.chapter}</div>
        </div>
      </div>
    </div>
  );
}

// ------------- Referral confirmation modal ---------------
function ReferralModal({ pending, onClose, onConfirm }) {
  const [count, setCount] = useState(1);
  const [type, setType] = useState("referral");
  const [value, setValue] = useState(25000);

  useEffect(() => { if (pending) { setCount(1); setType("referral"); setValue(25000); } }, [pending]);

  const f = pending ? findSeedMember(pending.from) : null;
  const t = pending ? findSeedMember(pending.to)   : null;

  return (
    <AnimatePresence>
      {pending && (
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
          className="w-full max-w-[440px]"
          onClick={(e) => e.stopPropagation()}
          data-testid="referral-modal"
        >
          <Panel className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <MonoLabel>Log Referral</MonoLabel>
                <div className="mt-3 flex items-center gap-3">
                  <PersonMini m={f} label="From" />
                  <ChevronsRight size={20} style={{ color: "var(--red-2)" }} />
                  <PersonMini m={t} label="To" />
                </div>
              </div>
              <button data-testid="referral-close-btn" onClick={onClose}><X size={18} /></button>
            </div>

            <div className="mt-6">
              <MonoLabel>Type</MonoLabel>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[
                  { id: "referral", label: "Referral" },
                  { id: "business", label: "Business" },
                  { id: "121",      label: "One-to-One" },
                ].map(t => (
                  <button
                    key={t.id}
                    data-testid={`referral-type-${t.id}`}
                    onClick={() => setType(t.id)}
                    className={`py-2 text-xs border ${type===t.id ? "border-[var(--red)] bg-[rgba(204,37,47,0.08)]" : "border-[var(--hairline)]"}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <MonoLabel>Count</MonoLabel>
              <div className="flex items-center gap-3">
                <button data-testid="referral-decrement-btn" onClick={() => setCount(Math.max(1, count-1))} className="btn-ghost !px-3 !py-2"><Minus size={14} /></button>
                <div data-testid="referral-count" className="font-serif text-3xl w-10 text-center">{count}</div>
                <button data-testid="referral-increment-btn" onClick={() => setCount(count+1)} className="btn-ghost !px-3 !py-2"><Plus size={14} /></button>
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between">
                <MonoLabel>Estimated value</MonoLabel>
                <div className="font-serif text-lg">₹{value.toLocaleString("en-IN")}</div>
              </div>
              <input data-testid="referral-value-slider" type="range" min={5000} max={500000} step={5000} value={value} onChange={(e)=>setValue(Number(e.target.value))} className="w-full mt-2 accent-[var(--red)]" />
            </div>

            <button data-testid="referral-confirm-btn" className="btn-red w-full mt-6" onClick={() => onConfirm(count, type, value)}>
              <Check size={14} /> Confirm & Log
            </button>
          </Panel>
        </motion.div>
      </motion.div>
      )}
    </AnimatePresence>
  );
}

function PersonMini({ m, label }) {
  return (
    <div className="flex items-center gap-2">
      <img src={m.avatar} className="w-9 h-9 object-cover border border-[var(--hairline)]" alt="" />
      <div className="min-w-0">
        <div className="mono-label" style={{ color: "var(--taupe)" }}>{label}</div>
        <div className="text-xs font-medium truncate max-w-[110px]">{m.name.split(' ')[0]}</div>
      </div>
    </div>
  );
}

function LeaderGate({ onOk }) {
  const [pin, setPin] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await verifyLeaderPin({ pin: pin.trim(), name: name.trim(), phone: phone.trim() });
      // Persist a server-side session before letting them in — audit trail is named, not anonymous.
      let session_id = null;
      try {
        const s = await startLeaderSession({ name: r.leader_name, phone: r.leader_phone });
        session_id = s.id;
      } catch { /* non-fatal — PIN passed */ }
      onOk({ name: r.leader_name, phone: r.leader_phone, session_id });
      if (r.warning) toast.info(r.warning);
    } catch (e) {
      const errs = e?.response?.data?.detail?.errors;
      setErr(errs ? errs[0].message : (e?.response?.data?.detail || "Verification failed"));
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen">
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-[420px] mx-auto px-4 sm:px-8 py-16">
        <Eyebrow>Table Leader Sign-in</Eyebrow>
        <h1 className="h-hero mt-3 text-3xl">Whose taps are these?</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--body)" }}>
          Enter the day's Leader PIN (from the organiser) and your name. Every referral you log will carry your name in the audit trail.
        </p>
        <Panel className="p-6 mt-6">
          <label className="block"><div className="mono-label mb-1">Your name</div>
            <input data-testid="leader-name" className="input-x !py-2" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
          </label>
          <label className="block mt-4"><div className="mono-label mb-1">Phone (optional)</div>
            <input data-testid="leader-phone" className="input-x !py-2" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit" />
          </label>
          <label className="block mt-4"><div className="mono-label mb-1">4-digit Leader PIN</div>
            <input data-testid="leader-pin" type="password" inputMode="numeric" maxLength={4}
              className="input-x !py-2 !tracking-widest !text-center !text-2xl"
              value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && submit()} />
          </label>
          {err && <div data-testid="leader-err" className="text-sm mt-3" style={{ color: "var(--red-2)" }}>{err}</div>}
          <button data-testid="leader-submit" onClick={submit} disabled={busy || !name || pin.length < 4}
            className="btn-red mt-5 w-full">
            <Check size={14} /> {busy ? "Verifying…" : "Start logging referrals"}
          </button>
          <div className="mono-label mt-4" style={{ color: "var(--muted)" }}>
            Your name persists on this device until you sign out.
          </div>
        </Panel>
      </div>
    </div>
  );
}
